import { buildOcPolicy, DEFAULT_OC_RULES, ocPolicy, parseOcRules, seatThresholds, type OcDerived, type OcGoalBasis, type OcPolicy, type OcRuleSource } from './oc-policy';
import { tpKey } from './oc-success';
import { gradient, predict, readSurrogate, type Surrogate } from './oc-surrogate';

/**
 * Requirements for every crime, worked out from tornprobability.com's success model (owner, 2026-10-22, round 3: "feel
 * free to make a blanket requirement but use our requirements as a baseline. This whole plan is to automate the role
 * requirements and find the best most optimal algorithm for it"; docs/oc-suitability.md section 9). Pure; the Omni-Torn
 * server runs it (apps/api/src/oc-requirements.ts — it asks tornprobability, a few calls a minute, cached for good) and
 * serves the result; the extension and the Discord bot judge seats by it where no rule says anything.
 *
 * 1. Calibrate on the rules in use ("anchors": the crimes whose every seat the rules give a number — in the built-in
 *    guidelines Break the Bank and Clinical Precision (level 8) and Blast from the Past (level 7); a "carries only" seat
 *    counts at the carry rate, 75). Their minimums give a chance of success in tornprobability's model; that is the chance
 *    the rules accept at the minimum for crimes of that level (the middle one when several share a level). Same with the
 *    targets (minimum + 5). Nothing to choose (owner, 2026-10-22: "Success chance percent should be derivable"): a crime
 *    of a level the rules cover gets that level's; between two covered levels, in proportion; above or below all of them,
 *    the nearest one's (no trend is assumed beyond what the rules show); level unknown, the middle of all of them
 *    (`goalsFor`). The cost shape θ (below) is the one whose solutions come closest to the rules' own numbers.
 * 2. For every other crime: the cheapest minimums that reach that chance of success — cheapest = the least
 *    Σ (minimum ÷ 100)^θ: θ = 1 counts every point the same (all of it would go on the heaviest seat), a large θ makes
 *    high minimums dear (every seat the same); θ in between spreads the requirement by how much each seat matters, the way
 *    the guidelines do (boost seats low, the seats that decide the crime high). Targets: the cheapest raise from the
 *    minimums that reaches the targets' chance of success.
 * 3. A crime that can't reach that chance even with 95% in every seat gets the same share of its own best (95% everywhere)
 *    as the guidelines' crimes have of theirs ("relaxed").
 * The search runs on the surrogate (oc-surrogate.ts); on the server its answers are then checked with tornprobability's
 * exact calculation and nudged up if they fall short. A faction's published rules (or a player's own lines) that number
 * crimes seat by seat are calibrated on the same way in the client, on the surrogates the server publishes
 * (`derivedForRules`): their requirements match THEIR rules' chance of success.
 */

/** The search box: requirements are whole numbers in [lo, hi] (the surrogate is fitted on 10–100). */
export const REQ_BOX = { lo: 10, hi: 95 } as const;
/** The carry rate a "carries only" seat counts at in the guidelines (their `carries: 75+`). */
export const CARRY_RATE = 75;
/** The cost shapes tried when calibrating (θ). */
export const THETAS = [1.25, 1.5, 2, 3, 4, 6, 8] as const;

const cost = (x: readonly number[], theta: number) => x.reduce((a, v) => a + (v / 100) ** theta, 0);
const c1 = (v: number, theta: number) => (v / 100) ** theta;

export interface CheapestResult {
  x: number[];
  /** The chance (by the function searched) at x. */
  success: number;
  /** Whether the goal was reachable within the box. */
  feasible: boolean;
}

/**
 * The cheapest whole-number lineup of requirements x (lo ≤ x ≤ hi per seat) with f(x) ≥ goal, cheapest = least
 * Σ (x/100)^θ. A Lagrangian search (minimise cost − λ f by coordinate descent, λ found by bisection) gets close; a local
 * search on whole numbers (drop a point where it still holds; move a point from one seat to another where that's cheaper)
 * finishes it. Not feasible: hi everywhere.
 */
export function cheapestLineup(f: (x: number[]) => number, o: { goal: number; theta: number; lo: readonly number[]; hi: readonly number[] }): CheapestResult {
  const n = o.lo.length;
  const lo = o.lo.map((v, i) => Math.min(v, o.hi[i]!));
  const hi = [...o.hi];
  if (f(hi) < o.goal) return { x: hi, success: f(hi), feasible: false };
  if (f(lo) >= o.goal) return { x: [...lo], success: f(lo), feasible: true };
  const theta = o.theta;
  // The chance is searched on a log scale: a crime's chance behaves like a product of checkpoint chances, whose logarithm
  // is concave — so minimising cost − λ·ln f has no gaps a plain f would leave (it jumps from "all low" to "all high").
  const lf = (x: number[]) => Math.log(Math.max(1e-9, f(x)));
  // Coordinate descent on cost − λ ln f, coarse (step 5) then fine (±5, step 1).
  const descend = (lambda: number, start: number[]): number[] => {
    const x = [...start];
    for (let sweep = 0; sweep < 20; sweep++) {
      let moved = false;
      for (let i = 0; i < n; i++) {
        if (lo[i] === hi[i]) continue;
        const val = (v: number) => {
          const keep = x[i]!;
          x[i] = v;
          const r = c1(v, theta) - lambda * lf(x);
          x[i] = keep;
          return r;
        };
        let best = x[i]!;
        let bv = val(best);
        for (let v = lo[i]!; v <= hi[i]!; v += 5) {
          const r = val(v);
          if (r < bv - 1e-12) [best, bv] = [v, r];
        }
        const a = Math.max(lo[i]!, best - 5);
        const b = Math.min(hi[i]!, best + 5);
        for (let v = a; v <= b; v++) {
          const r = val(v);
          if (r < bv - 1e-12) [best, bv] = [v, r];
        }
        if (best !== x[i]) {
          x[i] = best;
          moved = true;
        }
      }
      if (!moved) break;
    }
    return x;
  };
  let lamHi = 1;
  let xHi = descend(lamHi, hi);
  for (let k = 0; k < 40 && f(xHi) < o.goal; k++) {
    lamHi *= 2;
    xHi = descend(lamHi, xHi);
  }
  if (f(xHi) < o.goal) xHi = [...hi];
  let lamLo = lamHi / 1024;
  for (let k = 0; k < 18; k++) {
    const mid = Math.sqrt(lamLo * lamHi);
    const xm = descend(mid, xHi);
    if (f(xm) >= o.goal) [lamHi, xHi] = [mid, xm];
    else lamLo = mid;
  }
  const x = [...xHi];
  // Whole-number finish: first make sure it holds, then take every saving that keeps it.
  for (let guard = 0; f(x) < o.goal && guard < 2000; guard++) {
    let bi = -1;
    let br = -Infinity;
    const base = f(x);
    for (let i = 0; i < n; i++) {
      if (x[i]! >= hi[i]!) continue;
      x[i]!++;
      const r = (f(x) - base) / Math.max(1e-12, c1(x[i]!, theta) - c1(x[i]! - 1, theta));
      x[i]!--;
      if (r > br) [bi, br] = [i, r];
    }
    if (bi < 0) break;
    x[bi]!++;
  }
  for (let guard = 0; guard < 3000; guard++) {
    let best: { i: number; j: number; save: number } | null = null;
    for (let i = 0; i < n; i++) {
      if (x[i]! <= lo[i]!) continue;
      x[i]!--;
      if (f(x) >= o.goal) {
        const save = c1(x[i]! + 1, theta) - c1(x[i]!, theta);
        if (!best || save > best.save) best = { i, j: -1, save };
      } else {
        for (let j = 0; j < n; j++) {
          if (j === i || x[j]! >= hi[j]!) continue;
          const save = c1(x[i]! + 1, theta) - c1(x[i]!, theta) - (c1(x[j]! + 1, theta) - c1(x[j]!, theta));
          if (save <= 1e-12 || (best && save <= best.save)) continue;
          x[j]!++;
          if (f(x) >= o.goal) best = { i, j, save };
          x[j]!--;
        }
      }
      x[i]!++;
    }
    if (!best) break;
    x[best.i]!--;
    if (best.j >= 0) x[best.j]!++;
  }
  return { x, success: f(x), feasible: true };
}

// ---------------------------------------------------------------- the guidelines as numbers ("anchors")

/** tornprobability's seat name → Torn's ("Muscle 1" → "Muscle #1"; "Hacker" stays). */
export const tornSeatName = (tp: string): string => tp.trim().replace(/\s+(\d+)$/, ' #$1');

/** A crime whose every seat the guidelines give a number: its minimums and targets in the model's seat order. */
export interface Anchor {
  scenario: string;
  /** Seat names in tornprobability's order (its own spelling). */
  seats: string[];
  min: number[];
  target: number[];
  /** "carries only" seats (counted at the carry rate). */
  carry: boolean[];
  /** Whose rule gave each seat's numbers. */
  sources: OcRuleSource[];
}

/**
 * The guidelines' numbers for one crime, or null when some seat has none (then the crime isn't an anchor). Only rules for
 * that seat, role or crime count — not level-wide or general rates. A "carries only" seat without a number counts at the
 * carry rate (`carries: 75+`, else 75).
 */
export function guidelineAnchor(policy: OcPolicy, scenario: string, seats: readonly string[]): Anchor | null {
  const p = ocPolicy({ ...policy, low: 0, derived: null });
  const min: number[] = [];
  const target: number[] = [];
  const carry: boolean[] = [];
  const sources: OcRuleSource[] = [];
  for (const tp of seats) {
    const th = seatThresholds(p, scenario, tornSeatName(tp), null, null, null);
    const own = (lvl: string) => lvl === 'seat' || lvl === 'role-in-crime' || lvl === 'role';
    if (th.reserved && th.min === null) {
      const r = th.carries?.min ?? CARRY_RATE;
      min.push(r);
      target.push(r);
      carry.push(true);
      sources.push(th.reserved.source ?? 'own');
      continue;
    }
    if (th.min === null || !own(th.minFrom)) return null;
    min.push(th.min);
    target.push(Math.max(th.min, th.target ?? th.min));
    carry.push(!!th.reserved);
    sources.push(th.minRule?.source === 'own' || th.targetRule?.source === 'own' ? 'own' : (th.minRule?.source ?? 'own'));
  }
  return seats.length ? { scenario, seats: [...seats], min, target, carry, sources } : null;
}

/** The built-in guidelines (DEFAULT_OC_RULES) as a policy — what the server calibrates to. */
export function guidelinePolicy(): OcPolicy {
  const set = parseOcRules(DEFAULT_OC_RULES, 'builtin');
  return ocPolicy({ low: 0, rules: set.rules, flags: set.flags, order: set.order, prefer: set.prefer, carries: set.carries });
}

// ---------------------------------------------------------------- crime levels

/**
 * Torn's level (difficulty) of the crimes it has been seen for — Torn's Crimes tab in the owner's page captures
 * (2026-10-19: Break the Bank 8, Blast from the Past 7, Leave No Trace 6, Dish It Out 6, Market Forces 3) and his
 * guidelines ("the level 8s": Break the Bank and Clinical Precision). By tpKey. Only a fallback: the server learns every
 * crime's level from finished crimes (Torn's own `difficulty`), and the extension and the bot see it on each crime.
 */
export const OC_SCENARIO_LEVELS: Readonly<Record<string, number>> = { breakthebank: 8, clinicalprecision: 8, blastfromthepast: 7, leavenotrace: 6, dishitout: 6, marketforces: 3 };

/** A crime's level: from `known` (Torn's data, by tpKey), else the table above; null when not known. */
export function scenarioLevel(name: string, known?: Readonly<Record<string, number | null | undefined>> | null): number | null {
  const k = tpKey(name);
  const v = known?.[k] ?? OC_SCENARIO_LEVELS[k];
  return typeof v === 'number' && Number.isInteger(v) && v >= 1 && v <= 20 ? v : null;
}

// ---------------------------------------------------------------- calibration

/** One anchor crime, measured: the chance at its minimums, targets and 95% everywhere (exact on the server). */
export interface AnchorMeasure {
  anchor: Anchor;
  /** The surrogate's chance (for the θ fit). */
  f: (x: number[]) => number;
  minSuccess: number;
  targetSuccess: number;
  /** At REQ_BOX.hi in every seat. */
  maxSuccess: number;
  /** The crime's level (null: not known — then it counts only towards the overall level). */
  level?: number | null;
}

/** What the rules accept for crimes of one level. */
export interface LevelCalibration {
  level: number;
  minSuccess: number;
  targetSuccess: number;
  minShare: number;
  targetShare: number;
  /** The rules' crimes of this level it came from. */
  anchors: string[];
}

export interface Calibration {
  /** 'levels': worked out from the rules, level by level (always, unless the server's expert override sets a 'fixed' level). */
  mode: 'levels' | 'fixed';
  /** Across all the rules' crimes (the middle one): the chance accepted at the minimum, and at the target. */
  minSuccess: number;
  targetSuccess: number;
  /** For crimes that can't reach those: the same share of their own best (95% everywhere). */
  minShare: number;
  targetShare: number;
  /** Level by level (lowest first); empty with a fixed level or when no anchor's level is known. */
  levels: LevelCalibration[];
  theta: number;
  /** How far the solutions at each θ were from the rules' numbers (RMSE in points, over non-carry seats). */
  thetaErrors: Array<{ theta: number; rmse: number }>;
}

/** The middle value (the mean of the two middle ones for an even count). */
export const median = (v: readonly number[]): number => {
  const s = [...v].sort((a, b) => a - b);
  const m = Math.floor(s.length / 2);
  return s.length % 2 ? s[m]! : (s[m - 1]! + s[m]!) / 2;
};

/** The θ whose solutions for these anchors (at their own success) come closest to their numbers. */
export function fitTheta(anchors: readonly AnchorMeasure[], thetas: readonly number[] = THETAS): { theta: number; thetaErrors: Calibration['thetaErrors'] } {
  const thetaErrors: Calibration['thetaErrors'] = [];
  for (const theta of thetas) {
    let sq = 0;
    let k = 0;
    for (const a of anchors) {
      const n = a.anchor.seats.length;
      // The carry seats stay at the carry rate (they're a membership rule, not a number to fit); the rest are solved for
      // the anchor's own success, so only the shape is compared.
      const lo = a.anchor.carry.map((c, i) => (c ? a.anchor.min[i]! : REQ_BOX.lo));
      const hi = a.anchor.carry.map((c, i) => (c ? a.anchor.min[i]! : REQ_BOX.hi));
      const r = cheapestLineup(a.f, { goal: a.minSuccess, theta, lo, hi });
      for (let i = 0; i < n; i++) {
        if (a.anchor.carry[i]) continue;
        sq += (r.x[i]! - a.anchor.min[i]!) ** 2;
        k++;
      }
    }
    thetaErrors.push({ theta, rmse: k ? Math.sqrt(sq / k) : 0 });
  }
  const best = [...thetaErrors].sort((a, b) => a.rmse - b.rmse || a.theta - b.theta)[0];
  return { theta: best?.theta ?? 2, thetaErrors };
}

const shareOf = (a: AnchorMeasure, k: 'minSuccess' | 'targetSuccess') => (a.maxSuccess > 0 ? Math.min(1, a[k] / a.maxSuccess) : 1);

/**
 * The success levels and the cost shape, from the anchors: level by level (the middle one where several crimes share a
 * level), and across all of them. `fixed` (0…1, the server's expert override OC_REQ_SUCCESS) sets the minimum's level by
 * hand for every crime (the target keeps the anchors' gap above it). `theta`: use this cost shape instead of fitting one.
 * Null without anchors.
 */
export function calibrate(anchors: readonly AnchorMeasure[], o: { fixed?: number | null; thetas?: readonly number[]; theta?: number | null } = {}): Calibration | null {
  if (!anchors.length) return null;
  const fixed = typeof o.fixed === 'number' && o.fixed > 0 && o.fixed < 1 ? o.fixed : null;
  let minSuccess = median(anchors.map((a) => a.minSuccess));
  let targetSuccess = median(anchors.map((a) => a.targetSuccess));
  if (fixed !== null) {
    targetSuccess = Math.min(0.999, fixed + Math.max(0, targetSuccess - minSuccess));
    minSuccess = fixed;
  }
  targetSuccess = Math.max(minSuccess, targetSuccess);
  const levels: LevelCalibration[] = [];
  if (fixed === null) {
    const by = new Map<number, AnchorMeasure[]>();
    for (const a of anchors) if (typeof a.level === 'number') by.set(a.level, [...(by.get(a.level) ?? []), a]);
    for (const [level, list] of [...by].sort((a, b) => a[0] - b[0])) {
      const ms = median(list.map((a) => a.minSuccess));
      levels.push({ level, minSuccess: ms, targetSuccess: Math.max(ms, median(list.map((a) => a.targetSuccess))), minShare: median(list.map((a) => shareOf(a, 'minSuccess'))), targetShare: median(list.map((a) => shareOf(a, 'targetSuccess'))), anchors: list.map((a) => a.anchor.scenario) });
    }
  }
  const t = typeof o.theta === 'number' && o.theta > 0 ? { theta: o.theta, thetaErrors: [] } : fitTheta(anchors, o.thetas);
  return { mode: fixed !== null ? 'fixed' : 'levels', minSuccess, targetSuccess, minShare: median(anchors.map((a) => shareOf(a, 'minSuccess'))), targetShare: median(anchors.map((a) => shareOf(a, 'targetSuccess'))), levels, ...t };
}

/** The chance of success a crime's requirements aim for, and where it came from. */
export interface Goals {
  minSuccess: number;
  targetSuccess: number;
  minShare: number;
  targetShare: number;
  basis: OcGoalBasis;
}

/**
 * What the rules accept for a crime of this level: that level's (the rules cover it); between the two covered levels
 * around it, in proportion; the nearest covered level's when it's above or below all of them (crimes are harder the
 * higher the level, but how much more the rules would accept isn't something they show — so no trend is carried on);
 * across all their crimes when its level isn't known.
 */
export function goalsFor(cal: Pick<Calibration, 'mode' | 'minSuccess' | 'targetSuccess' | 'minShare' | 'targetShare'> & { levels?: readonly LevelCalibration[] }, level: number | null | undefined): Goals {
  const all = { minSuccess: cal.minSuccess, targetSuccess: cal.targetSuccess, minShare: cal.minShare, targetShare: cal.targetShare };
  if (cal.mode === 'fixed') return { ...all, basis: { kind: 'fixed' } };
  const lv = cal.levels ?? [];
  if (typeof level !== 'number' || !lv.length) return { ...all, basis: { kind: 'all' } };
  const pick = (l: LevelCalibration) => ({ minSuccess: l.minSuccess, targetSuccess: l.targetSuccess, minShare: l.minShare, targetShare: l.targetShare });
  const same = lv.find((l) => l.level === level);
  if (same) return { ...pick(same), basis: { kind: 'level', level } };
  const below = [...lv].reverse().find((l) => l.level < level);
  const above = lv.find((l) => l.level > level);
  if (below && above) {
    const w = (level - below.level) / (above.level - below.level);
    const mix = (k: 'minSuccess' | 'targetSuccess' | 'minShare' | 'targetShare') => below[k] + (above[k] - below[k]) * w;
    const minSuccess = mix('minSuccess');
    return { minSuccess, targetSuccess: Math.max(minSuccess, mix('targetSuccess')), minShare: mix('minShare'), targetShare: mix('targetShare'), basis: { kind: 'between', lo: below.level, hi: above.level } };
  }
  const near = (below ?? above)!;
  return { ...pick(near), basis: { kind: 'nearest', level: near.level } };
}

/** What a crime needs: minimums and targets in the model's seat order, the goals used, and whether they were relaxed. */
export interface Derivation {
  min: number[];
  target: number[];
  minGoal: number;
  targetGoal: number;
  relaxed: boolean;
  /** tornprobability's exact chance at the minimums / targets (null: not checked). */
  minSuccess: number | null;
  targetSuccess: number | null;
  /** Exact calls the check used. */
  checks: number;
}

/** The goals and the cost shape a derivation needs (a Calibration has them; so has Goals + θ). */
export type GoalsTheta = Pick<Goals, 'minSuccess' | 'targetSuccess' | 'minShare' | 'targetShare'> & { theta: number };

/** The chance to reach at the minimums and the targets: the accepted one, or the same share of the crime's best. */
export function goalsAt(g: Pick<Goals, 'minSuccess' | 'targetSuccess' | 'minShare' | 'targetShare'>, maxSuccess: number): { minGoal: number; targetGoal: number } {
  const minGoal = Math.min(g.minSuccess, g.minShare * maxSuccess);
  return { minGoal, targetGoal: Math.max(minGoal, Math.min(g.targetSuccess, g.targetShare * maxSuccess)) };
}

/** deriveRequirements without exact checks (the client: surrogate only), at once. */
export function deriveOnSurrogate(fhat: (x: number[]) => number, n: number, cal: GoalsTheta, maxSuccess: number): { min: number[]; target: number[]; relaxed: boolean } {
  const lo = new Array<number>(n).fill(REQ_BOX.lo);
  const hi = new Array<number>(n).fill(REQ_BOX.hi);
  const { minGoal, targetGoal } = goalsAt(cal, maxSuccess);
  const m = cheapestLineup(fhat, { goal: minGoal, theta: cal.theta, lo, hi });
  const t = cheapestLineup(fhat, { goal: targetGoal, theta: cal.theta, lo: m.x, hi });
  return { min: m.x, target: t.x.map((v, i) => Math.max(v, m.x[i]!)), relaxed: minGoal < cal.minSuccess - 1e-9 };
}

/**
 * One crime's requirements from its surrogate `fhat`, checked with `exact` (tornprobability's own answer; null = not
 * available): if the exact chance at the solution falls short of the goal, the goal for the search is raised by the
 * shortfall and it searches again (at most `rounds` times). `maxSuccess`: the chance at 95% everywhere (exact when known).
 */
export async function deriveRequirements(fhat: (x: number[]) => number, n: number, cal: GoalsTheta, maxSuccess: number, exact: ((x: number[]) => Promise<number | null>) | null, rounds = 3): Promise<Derivation> {
  const lo = new Array<number>(n).fill(REQ_BOX.lo);
  const hi = new Array<number>(n).fill(REQ_BOX.hi);
  const { minGoal, targetGoal } = goalsAt(cal, maxSuccess);
  let checks = 0;
  const solve = async (goal: number, lower: number[]): Promise<{ x: number[]; exact: number | null }> => {
    let aim = goal;
    let r = cheapestLineup(fhat, { goal: aim, theta: cal.theta, lo: lower, hi });
    let got: number | null = null;
    let checked = false;
    for (let k = 0; k < rounds && exact; k++) {
      got = await exact(r.x);
      checks++;
      checked = true;
      if (got === null || got >= goal - 0.0005 || !r.feasible) break;
      // Short of the goal by tornprobability's own answer: aim higher by the shortfall and search again.
      let next = r;
      for (let t = 0; t < 12 && next.x.every((v, i) => v === r.x[i]) && aim < 0.9999; t++) {
        aim = Math.min(0.9999, aim + (goal - got) + 0.002);
        next = cheapestLineup(fhat, { goal: aim, theta: cal.theta, lo: lower, hi });
      }
      if (next.x.every((v, i) => v === r.x[i])) break;
      r = next;
      got = null;
      checked = false;
    }
    if (!checked && exact) {
      got = await exact(r.x);
      checks++;
    }
    return { x: r.x, exact: got };
  };
  const m = await solve(minGoal, lo);
  const t = await solve(targetGoal, m.x);
  return { min: m.x, target: t.x.map((v, i) => Math.max(v, m.x[i]!)), minGoal, targetGoal, relaxed: minGoal < cal.minSuccess - 1e-9, minSuccess: m.exact, targetSuccess: t.exact, checks };
}

// ---------------------------------------------------------------- the published document

export interface DerivedCrime {
  /** As tornprobability writes it ("Break the Bank"). */
  name: string;
  /** Seat names in its model's order ("Muscle 1"). */
  seats: string[];
  min: number[];
  target: number[];
  /** tornprobability's exact chance at the minimums / targets / 95% everywhere (null: not asked). */
  minSuccess: number | null;
  targetSuccess: number | null;
  maxSuccess: number | null;
  relaxed: boolean;
  /**
   * tornprobability's model of this crime, fitted (oc-surrogate.ts: coefficients, and how far off it was — `err`): for
   * "what if" lineups (labelled ≈) and for clients to work out requirements for their faction's own rules without asking
   * tornprobability. Null when it couldn't be fitted.
   */
  surrogate: Surrogate | null;
  /** Fingerprint of the crime's seats and role weights the surrogate was fitted for (a new one = a refit). */
  fp?: string;
  /** Torn's level of the crime (null: not known). */
  level?: number | null;
  /** The chance of success its numbers were matched to, and where that came from (goalsFor). */
  goal?: { minSuccess: number; targetSuccess: number; basis: OcGoalBasis } | null;
  /** An anchor: the guidelines' own numbers (they win; these are for comparison). */
  guideline?: { min: number[]; target: number[]; minSuccess: number | null; targetSuccess: number | null } | null;
}

export interface OcRequirementsDoc {
  v: 1;
  /** When derived (ms). */
  at: number;
  calibration: (Omit<Calibration, 'thetaErrors'> & { thetaErrors?: Calibration['thetaErrors']; anchors: string[] }) | null;
  /** By tpKey of the crime's name. */
  crimes: Record<string, DerivedCrime>;
  /** Calls to tornprobability this run made / answers it found kept; scenarios it couldn't do (and why). */
  stats?: { calls: number; cached: number; skipped: Array<{ name: string; why: string }>; done: number; total: number };
}

const rateArr = (x: unknown, n: number): number[] | null => (Array.isArray(x) && x.length === n && x.every((v) => typeof v === 'number' && Number.isFinite(v) && v >= 0 && v <= 100) ? (x as number[]).map((v) => Math.round(v)) : null);
const chance = (x: unknown): number | null => (typeof x === 'number' && Number.isFinite(x) && x >= 0 && x <= 1 ? x : null);

/** Only a well-formed document (from the server's answer or a file); crimes that don't read are dropped. */
export function readRequirementsDoc(x: unknown): OcRequirementsDoc | null {
  const o = x && typeof x === 'object' && !Array.isArray(x) ? (x as Record<string, unknown>) : null;
  if (!o || o.v !== 1 || typeof o.at !== 'number') return null;
  const crimes: Record<string, DerivedCrime> = {};
  for (const [k, raw] of Object.entries((o.crimes && typeof o.crimes === 'object' ? o.crimes : {}) as Record<string, unknown>)) {
    const c = raw && typeof raw === 'object' ? (raw as Record<string, unknown>) : null;
    const seats = Array.isArray(c?.seats) && c!.seats.every((s) => typeof s === 'string') ? (c!.seats as string[]).map((s) => s.slice(0, 60)).slice(0, 12) : null;
    if (!c || !seats?.length || typeof c.name !== 'string') continue;
    const min = rateArr(c.min, seats.length);
    const target = rateArr(c.target, seats.length);
    if (!min || !target) continue;
    const sur = readSurrogate(c.surrogate);
    const gl = c.goal && typeof c.goal === 'object' ? (c.goal as Record<string, unknown>) : null;
    const gms = chance(gl?.minSuccess);
    const gts = chance(gl?.targetSuccess);
    const basis = readBasis(gl?.basis);
    const level = typeof c.level === 'number' && Number.isInteger(c.level) && c.level >= 1 && c.level <= 20 ? c.level : null;
    const g = c.guideline && typeof c.guideline === 'object' ? (c.guideline as Record<string, unknown>) : null;
    const gm = g ? rateArr(g.min, seats.length) : null;
    const gt = g ? rateArr(g.target, seats.length) : null;
    crimes[tpKey(k)] = {
      name: c.name.slice(0, 60),
      seats,
      min,
      target: target.map((v, i) => Math.max(v, min[i]!)),
      minSuccess: chance(c.minSuccess),
      targetSuccess: chance(c.targetSuccess),
      maxSuccess: chance(c.maxSuccess),
      relaxed: c.relaxed === true,
      surrogate: sur && sur.n === seats.length ? sur : null,
      ...(typeof c.fp === 'string' && c.fp.length <= 40 ? { fp: c.fp } : {}),
      level,
      ...(gms !== null && gts !== null && basis ? { goal: { minSuccess: gms, targetSuccess: Math.max(gms, gts), basis } } : {}),
      ...(gm && gt ? { guideline: { min: gm, target: gt, minSuccess: chance(g!.minSuccess), targetSuccess: chance(g!.targetSuccess) } } : {}),
    };
  }
  const cal = o.calibration && typeof o.calibration === 'object' ? (o.calibration as Record<string, unknown>) : null;
  const ms = chance(cal?.minSuccess);
  const ts = chance(cal?.targetSuccess);
  const levels: LevelCalibration[] = [];
  for (const l of Array.isArray(cal?.levels) ? (cal!.levels as unknown[]).slice(0, 20) : []) {
    const o = l && typeof l === 'object' ? (l as Record<string, unknown>) : null;
    const lms = chance(o?.minSuccess);
    const lts = chance(o?.targetSuccess);
    if (!o || typeof o.level !== 'number' || !Number.isInteger(o.level) || lms === null || lts === null) continue;
    levels.push({ level: o.level, minSuccess: lms, targetSuccess: Math.max(lms, lts), minShare: chance(o.minShare) ?? 1, targetShare: chance(o.targetShare) ?? 1, anchors: Array.isArray(o.anchors) ? o.anchors.filter((a): a is string => typeof a === 'string').slice(0, 20) : [] });
  }
  levels.sort((a, b) => a.level - b.level);
  const calibration =
    cal && ms !== null && ts !== null
      ? {
          mode: (cal.mode === 'fixed' ? 'fixed' : 'levels') as Calibration['mode'],
          minSuccess: ms,
          targetSuccess: ts,
          minShare: chance(cal.minShare) ?? 1,
          targetShare: chance(cal.targetShare) ?? 1,
          levels,
          theta: typeof cal.theta === 'number' && cal.theta > 0 && cal.theta < 50 ? cal.theta : 2,
          anchors: Array.isArray(cal.anchors) ? cal.anchors.filter((a): a is string => typeof a === 'string').slice(0, 40) : [],
        }
      : null;
  return { v: 1, at: o.at, calibration, crimes };
}

function readBasis(x: unknown): OcGoalBasis | null {
  const o = x && typeof x === 'object' ? (x as Record<string, unknown>) : null;
  const lv = (v: unknown) => typeof v === 'number' && Number.isInteger(v) && v >= 1 && v <= 20;
  if (o?.kind === 'level' && lv(o.level)) return { kind: 'level', level: o.level as number };
  if (o?.kind === 'nearest' && lv(o.level)) return { kind: 'nearest', level: o.level as number };
  if (o?.kind === 'between' && lv(o.lo) && lv(o.hi)) return { kind: 'between', lo: o.lo as number, hi: o.hi as number };
  if (o?.kind === 'all' || o?.kind === 'fixed') return { kind: o.kind };
  return null;
}

const seatMap = (seats: readonly string[], min: readonly number[], target: readonly number[]) => {
  const out: Record<string, { min: number; target: number }> = {};
  seats.forEach((s, i) => (out[tpKey(s)] = { min: min[i]!, target: target[i]! }));
  return out;
};

/**
 * The document → what the policy needs (oc-policy.ts `derived`): per crime, per seat (both by tpKey), the minimum and
 * target; plus the success levels for the words (per crime where the document has them). Matched to the built-in
 * guidelines. Null when there's nothing derived.
 */
export function derivedFromDoc(doc: OcRequirementsDoc | null | undefined): OcDerived | null {
  if (!doc?.calibration || !Object.keys(doc.crimes).length) return null;
  const crimes: OcDerived['crimes'] = {};
  for (const [k, c] of Object.entries(doc.crimes)) {
    const g = c.goal ?? (doc.calibration.mode === 'fixed' ? { ...goalsFor(doc.calibration, null) } : null);
    crimes[k] = { seats: seatMap(c.seats, c.min, c.target), relaxed: c.relaxed, ...(g ? { minSuccess: g.minSuccess, targetSuccess: g.targetSuccess, basis: g.basis } : {}) };
  }
  return { crimes, minSuccess: doc.calibration.minSuccess, targetSuccess: doc.calibration.targetSuccess, at: doc.at, source: 'builtin' };
}

// ---------------------------------------------------------------- the rules in use (a faction's, a player's own)

const memo = new Map<string, OcDerived | null>();
const MEMO_MAX = 16;

/**
 * Requirements matched to the rules in use (owner, 2026-10-22: factions that publish their own rules get requirements
 * matched to THEIR rules' chance of success): the crimes those rules number seat by seat are the anchors; their chance of
 * success comes from the surrogates the server publishes (no request to tornprobability), level by level as on the
 * server (goalsFor), with the cost shape fitted to those rules; every other crime with a surrogate is solved for it.
 * The server's own set stays where nothing changes: rules with no crime numbered seat by seat, or the same numbers as
 * the built-in guidelines (then it's the server's set, checked against tornprobability exactly), and for crimes without
 * a surrogate. `levels`: crime levels seen in Torn's data (by tpKey), over the document's own. Remembered per document
 * and rules, so it's worked out once.
 */
export function derivedForRules(doc: OcRequirementsDoc | null | undefined, policy: OcPolicy, o: { fromFaction?: boolean; levels?: Readonly<Record<string, number | null | undefined>> | null } = {}): OcDerived | null {
  const base = derivedFromDoc(doc);
  if (!doc || !base) return base;
  const anchors: Array<{ k: string; c: DerivedCrime; a: Anchor }> = [];
  for (const [k, c] of Object.entries(doc.crimes)) {
    const a = guidelineAnchor(policy, c.name, c.seats);
    if (a) anchors.push({ k, c, a });
  }
  if (!anchors.length) return base;
  const source: OcRuleSource = anchors.some((x) => x.a.sources.includes('own')) ? 'own' : o.fromFaction ? 'faction' : 'builtin';
  // The same anchors as the server's (the built-in guidelines' numbers): the server's set, as checked.
  const cal = doc.calibration!;
  const serverAnchors = Object.entries(doc.crimes).filter(([, c]) => !!c.guideline && cal.anchors.some((n) => tpKey(n) === tpKey(c.name)));
  const same =
    anchors.length === serverAnchors.length &&
    anchors.every(({ k, a }) => {
      const g = doc.crimes[k]?.guideline;
      return serverAnchors.some(([sk]) => sk === k) && !!g && g.min.join() === a.min.join() && g.target.join() === a.target.join();
    });
  if (same) return { ...base, source };
  const levelOf = (c: DerivedCrime) => scenarioLevel(c.name, { ...Object.fromEntries(Object.entries(doc.crimes).map(([k, x]) => [k, x.level ?? null])), ...(o.levels ?? {}) });
  const key = JSON.stringify([doc.at, source, anchors.map((x) => [x.k, x.a.min, x.a.target, levelOf(x.c)]), Object.entries(doc.crimes).map(([k, c]) => [k, levelOf(c)])]);
  if (memo.has(key)) return memo.get(key)!;
  const measured: AnchorMeasure[] = [];
  for (const { c, a } of anchors) {
    const sur = c.surrogate;
    if (!sur) continue;
    const f = (x: number[]) => predict(sur, x);
    measured.push({ anchor: a, f, minSuccess: f(a.min), targetSuccess: f(a.target), maxSuccess: f(new Array<number>(a.seats.length).fill(REQ_BOX.hi)), level: levelOf(c) });
  }
  const mine = calibrate(measured);
  let out: OcDerived | null = base;
  if (mine) {
    const crimes: OcDerived['crimes'] = { ...base.crimes };
    const anchorKeys = new Set(anchors.map((x) => x.k));
    for (const [k, c] of Object.entries(doc.crimes)) {
      const sur = c.surrogate;
      // The rules number every seat of their own crimes: nothing derived there. A crime without a surrogate keeps the
      // server's numbers (and says whose they are).
      if (anchorKeys.has(k)) {
        delete crimes[k];
        continue;
      }
      if (!sur) {
        if (crimes[k]) crimes[k] = { ...crimes[k]!, source: 'builtin' };
        continue;
      }
      const g = goalsFor(mine, levelOf(c));
      const f = (x: number[]) => predict(sur, x);
      const d = deriveOnSurrogate(f, c.seats.length, { ...g, theta: mine.theta }, f(new Array<number>(c.seats.length).fill(REQ_BOX.hi)));
      crimes[k] = { seats: seatMap(c.seats, d.min, d.target), relaxed: d.relaxed, minSuccess: g.minSuccess, targetSuccess: g.targetSuccess, basis: g.basis };
    }
    out = { crimes, minSuccess: mine.minSuccess, targetSuccess: mine.targetSuccess, at: doc.at, source };
  }
  if (memo.size >= MEMO_MAX) memo.delete(memo.keys().next().value!);
  memo.set(key, out);
  return out;
}

/** What derivedForRules worked out (to keep between page loads — it takes a moment on a phone). */
export function derivedMemo(): Array<[string, OcDerived | null]> {
  return [...memo];
}

/** Worked-out requirements kept from an earlier page load (derivedMemo); anything malformed is skipped. */
export function seedDerivedMemo(entries: unknown): void {
  if (!Array.isArray(entries)) return;
  for (const e of entries.slice(-MEMO_MAX)) {
    if (!Array.isArray(e) || typeof e[0] !== 'string' || e[0].length > 20_000 || memo.has(e[0])) continue;
    const v = e[1] as OcDerived | null;
    if (v !== null && !(v && typeof v === 'object' && v.crimes && typeof v.crimes === 'object' && typeof v.minSuccess === 'number' && typeof v.targetSuccess === 'number')) continue;
    if (memo.size >= MEMO_MAX) memo.delete(memo.keys().next().value!);
    memo.set(e[0], v);
  }
}

/**
 * buildOcPolicy with requirements derived for the rules in use (derivedForRules): own > faction > guidelines > derived
 * (matched to the rules in use) > general rate > no rule. `requirements`: the server's document (GET
 * /api/oc/requirements, or its file for the bot).
 */
export function buildPolicyWithRequirements(o: Parameters<typeof buildOcPolicy>[0] & { requirements?: OcRequirementsDoc | null; levels?: Readonly<Record<string, number | null | undefined>> | null }): ReturnType<typeof buildOcPolicy> {
  const built = buildOcPolicy({ ...o, derived: null });
  const derived = o.requirements ? derivedForRules(o.requirements, built.policy, { fromFaction: built.fromFaction, levels: o.levels ?? null }) : (o.derived ?? null);
  return { ...built, policy: { ...built.policy, derived } };
}

/** A surrogate is trusted for "what if" numbers (shown with ≈) when it was never more than this far off on held-out lineups. */
export const WHATIF_MAX_ERROR = 0.03;

/**
 * The crime's ≈ chance for a lineup by Torn's seat names (null: no surrogate, a seat missing, or not trusted enough when
 * `trusted`). Seats are matched to the model's order by tpKey ("Muscle #1" = "Muscle 1").
 */
export function approxSuccess(doc: OcRequirementsDoc | null | undefined, scenario: string, seats: ReadonlyArray<{ role: string; rate: number | null }>, trusted = false): number | null {
  const c = doc?.crimes[tpKey(scenario)];
  const s = c?.surrogate;
  if (!c || !s) return null;
  if (trusted && (s.err.heldMax === null || s.err.heldMax > WHATIF_MAX_ERROR)) return null;
  const by = new Map(seats.map((x) => [tpKey(x.role), x.rate]));
  const x: number[] = [];
  for (const name of c.seats) {
    const r = by.get(tpKey(name));
    if (typeof r !== 'number' || !Number.isFinite(r)) return null;
    x.push(Math.max(0, Math.min(100, r)));
  }
  return predict(s, x);
}

/** A crime's ≈ success as a function of rates by Torn's seat name, and each seat's ≈ gain per point at `at` (lineup use). */
export function successModel(doc: OcRequirementsDoc | null | undefined, scenario: string): { f: (rates: ReadonlyMap<string, number>) => number | null; gain: (rates: ReadonlyMap<string, number>) => Map<string, number> | null } | null {
  const c = doc?.crimes[tpKey(scenario)];
  const s = c?.surrogate;
  if (!c || !s) return null;
  const vec = (rates: ReadonlyMap<string, number>): number[] | null => {
    const by = new Map([...rates].map(([k, v]) => [tpKey(k), v]));
    const x: number[] = [];
    for (const name of c.seats) {
      const r = by.get(tpKey(name));
      if (typeof r !== 'number' || !Number.isFinite(r)) return null;
      x.push(r);
    }
    return x;
  };
  return {
    f: (rates) => {
      const x = vec(rates);
      return x ? predict(s, x) : null;
    },
    gain: (rates) => {
      const x = vec(rates);
      if (!x) return null;
      const g = gradient((v) => predict(s, v), x);
      return new Map(c.seats.map((name, i) => [tpKey(name), Math.max(0, g[i]!)]));
    },
  };
}

/** For words: "Robber 60 · Muscle 1 55 · …". */
export const seatList = (seats: readonly string[], v: readonly number[]): string => seats.map((s, i) => `${tornSeatName(s)} ${v[i]}`).join(' · ');

/** Total cost of a lineup of requirements (for reports). */
export const requirementCost = cost;
